import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers(['root', 'container', 'leftRegion', 'rightRegion', 'item']);

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *  keys: ("root" | "container" | "leftRegion" | "rightRegion")[]
 * }>} SectionPanelStyles
 */
export const sectionPanelStyles = helpers.defineMultiStyleConfig({
  /**
   * @param {Object} args - Component arguments
   * @param {Contentful.SectionPanel} args.component - SectionPanel component data
   * @param {boolean} [args.isComplete] - Whether component has all fields
   * @param {boolean} [args.isFetching] - Whether additional data is being fetched
   *
   * @returns {SectionPanelStyles} - Component styles
   */
  baseStyle: ({component}) => {
    return {
      root: {
      },
      container: {
      },
      leftRegion: {
      },
      rightRegion: {
      },
    };
  },
  variants: {
    'Split Heading': (args) => ({
      container: {
        paddingTop: {base: 15, md: args.component.paddingTop || 30},
        paddingBottom: {base: 10, md: args.component.paddingBottom || 16},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 4, md: 10},
        sx: {
          '.component--rich-text-block h1, .component--rich-text-block h2, .component--rich-text-block h3, .component--rich-text-block h4, .component--rich-text-block h5, .component--rich-text-block h6': {
            fontSize: {base: '2.75xl', md: '4.75xl'},
            fontWeight: '500',
            lineHeight: '120%',
          },
          '.component--rich-text-block .chakra-text': {
            fontSize: {base: 'md', md: 'lg'},
            fontWeight: '400',
            lineHeight: '160%',
          },
          '.chakra-link': {
            textDecoration: 'none',
            color: 'black',
            fontSize: 'sm',
            fontWeight: '400',
            lineHeight: '120%',
            paddingY: 3.5,
            paddingX: 12,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'black',
            marginTop: 2,
            _hover: {
              backgroundColor: 'black',
              color: 'white',
              textDecoration: 'underline',
            },
          },
        },
      },
      leftRegion: {
        flex: {base: '1', md: '1'},
      },
      rightRegion: {
        flex: {base: '1', md: '1'},
      },
    }),
    '50/50': (args) => ({
      container: {
        maxWidth: 'container.xxl',
        marginX: 'auto',
        paddingTop: {base: 14, md: 20},
        paddingBottom: {base: 10, md: 17.5},
        paddingLeft: {base: 5, md: 20},
        paddingRight: {base: 5, md: 20},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 0, md: 10},
        sx: {
          '.amb-markup p:empty': {
            display: 'none',
          },
          '.component--rich-text-block': {
            marginBottom: 6,
          },
          '.amb-markup p': {
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: {base: 'md', md: 'lg'},
            lineHeight: {base: '150%', md: '160%'},
          },
          '.amb-markup h2': {
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: {base: '2.75xl', md: '4.75xl'},
            lineHeight: '120%',
            color: 'brandColors.greyscale90',
            letterSpacing: {base: 'headingTight', md: 'headingTighter'},
          },
          '.component--action-links a p': {
            fontSize: 'sm',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '120%',
            color: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 6,
          },
          '.component--action-links a:hover': {
            textDecoration: 'none',
          },
          '.component--action-links a:hover p': {
            color: 'white',
          },
          '.amb-markup a p': {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: 'sm',
            lineHeight: '120%',
          },
          '.chakra-link, .component--action-links .chakra-link': {
            paddingY: 3.5,
            paddingX: 12,
          },
        },
      },
      leftRegion: {
        flex: '1',
      },
      rightRegion: {
        flex: '1',
      },
    }),
    '60/40': (args) => ({
      container: {
        paddingTop: {base: 15, md: args.component.paddingTop || 30},
        paddingBottom: {base: 10, md: args.component.paddingBottom || 17.5},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 0, md: 10},
      },
      leftRegion: {
        flex: {base: '1', md: '0 1 60%'},
      },
      rightRegion: {
        flex: {base: '1', md: '0 1 40%'},
      },
    }),
    '65/35': (args) => ({
      container: {
        paddingTop: {base: 15, md: args.component.paddingTop || 30},
        paddingBottom: {base: 10, md: args.component.paddingBottom || 17.5},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 0, md: 10},
      },
      leftRegion: {
        flex: {base: '1', md: '0 1 65%'},
      },
      rightRegion: {
        flex: {base: '1', md: '0 1 35%'},
      },
    }),
    'King Difference Modular preview Block - (CF-013)': (args) => ({
      container: {
        paddingTop: {base: 12, md: args.component.paddingTop || 30},
        paddingBottom: {base: 2, md: args.component.paddingBottom || 13.25},
        sx: {
          '.component--rich-text-block': {
            textAlign: 'center',
            paddingLeft: {base: 0, md: 75},
            paddingRight: {base: 0, md: 75},
            paddingBottom: {base: 2, md: 13.25},
          },
          '.component--rich-text-block .chakra-text:first-of-type': {
            textTransform: 'uppercase',
            fontSize: {base: 'xs', md: 'sm'},
            fontWeight: '500',
            lineHeight: '140%',
            letterSpacing: {base: 'eyebrow', md: 'eyebrowLg'},
          },
          '.component--rich-text-block h1': {
            fontSize: {base: '2.75xl', md: '4.6xl'},
            fontWeight: '500',
            lineHeight: '120%',
          },
          '.component--rich-text-block .chakra-text:not(:first-of-type)': {
            fontSize: {base: 'md', md: 'xl'},
            fontWeight: '400',
            lineHeight: '150%',
          },
        },
      },
    }),
    'Support Block - (CF-019)': (args) => ({
      container: {
        display: {base: 'flex', lg: 'grid'},
        flexDirection: 'column',
        gridTemplateColumns: {base: 'repeat(1, 1fr)', lg: 'var(--chakra-sizes-supportSidebar, 290px) 1fr 1fr'},
        gap: {base: 5, lg: 10},
        paddingTop: {base: 10, lg: args.component.paddingTop || 20},
        paddingBottom: {base: 10, lg: args.component.paddingBottom || 32.5},
        sx: {
          '> :last-of-type': {
            order: {base: '-1', lg: '1'},
          },
          '.component--container': {
            paddingX: 0,
          },
          '.component--container ul': {
            marginLeft: 7.5,
          },
          '.component--container .component--rich-text-block': {
            marginBottom: 6,
          },
          '.component--container .chakra-link': {
            backgroundColor: 'transparent',
            fontSize: 'sm',
            fontWeight: '600',
            lineHeight: '120%',
            textAlign: 'center',
            width: {base: '100%', lg: 'auto'},
            marginRight: {base: 0, lg: 3.5},
            marginBottom: {base: 2, lg: 3.5},
          },
          // Site Calendly off or showroom store flag: hide only Calendly hash links in Support Block
          '&[data-hide-calendly-booking-links="true"] a[href$="#calendly-in-person"], &[data-hide-calendly-booking-links="true"] a[href$="#calendly-video"]':
            {
              display: 'none !important',
            },
          '.component--container .chakra-link .chakra-text': {
            fontSize: 'sm',
            fontWeight: '600',
          },
          '.component--container a:last-of-type': {
            marginBottom: 0,
            marginRight: 0,
          },
          '.component--container .chakra-text': {
            color: 'brandColors.greyscale90',
            fontSize: 'md',
            fontWeight: '400',
            lineHeight: '160%',
          },
          '.component--rich-text-block h1, .component--rich-text-block h2, .component--rich-text-block h3, .component--rich-text-block h4, .component--rich-text-block h5, .component--rich-text-block h6': {
            color: 'brandColors.greyscale90',
            fontSize: {base: '2.75xl', lg: '4.75xl'},
            fontWeight: '500',
            lineHeight: '120%',
          },
          '.component--image img': {
            display: 'block',
            width: '100%',
            maxWidth: '100%',
          },
        },
      },
    }),
    'Editorial Feature Block - (CF-020) [1]': (args) => ({
      container: {
        paddingTop: {base: 4, md: args.component.paddingTop || 10},
        paddingBottom: {base: 4, md: args.component.paddingBottom || 10},
        sx: {
          '.component--container .component--rich-text-block': {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          },
        },
      },
    }),
    'Editorial Feature Block - (CF-020) [2]': (args) => ({
      container: {
        paddingTop: {base: 4, md: args.component.paddingTop || 10},
        paddingBottom: {base: 4, md: args.component.paddingBottom || 10},
        flexDirection: {base: 'column', md: 'row'},
        gap: {base: 0, md: 10},
      },
      leftRegion: {
        flex: {base: '1', md: '0 0 60%'},
        minW: {md: 0},
      },
      rightRegion: {
        flex: {base: '1', md: '0 0 40%'},
        minW: {md: 0},
      },
    }),
    'Editorial Feature Block - (CF-020) [3]': (args) => ({
      container: {
        paddingTop: {base: 4, md: args.component.paddingTop || 10},
        paddingBottom: {base: 4, md: args.component.paddingBottom || 10},
        gridTemplateColumns: {base: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)'},
        gap: {base: 5, md: 10},
      },
    }),
    'Editorial Copy - (CF-021)': (args) => ({
      container: {
        paddingTop: {base: 5, md: args.component.paddingTop || 10},
        paddingBottom: {base: 5, md: args.component.paddingBottom || 10},
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        sx: {
          '.component--rich-text-block': {
            width: {base: '100%', md: 'editorial'},
          },
          '.component--rich-text-block h1': {
            fontSize: {base: '1.5xl', md: '3.25xl'},
            fontWeight: '500',
            lineHeight: '120%',
            letterSpacing: '0',
          },
          '.component--rich-text-block > p.chakra-text': {
            fontSize: {base: 'md', md: 'lg'},
            fontWeight: '400',
            lineHeight: '160%',
            letterSpacing: '0',
          },
          '& .rich-text-content.amb-markup': {
            '& blockquote': {
              padding: {base: '0 0 0 6', md: '0 0 0 28px'},
              borderLeftWidth: '4px',
              borderLeftStyle: 'solid',
              borderColor: 'brandColors.primaryDarkLinen',
              fontSize: {base: 'xl', md: '2.75xl'},
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: {base: '150%', md: '140%'},
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              lineHeight: '120%',
              mb: 4,
            },
            '& p': {
              mb: 4,
            },
            '& p, & blockquote': {
              whiteSpace: 'pre-line',
            },
          },
        },
      },
    }),
    'Range/Category Tile Grid Block - (CF-030) [1]': (args) => ({
      container: {
        gridTemplateColumns: {base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)'},
        gap: 0,
        sx: {
          '> :last-of-type': {
            order: {base: '-1', md: '1'},
          },
        },
      },
    }),
    'Range/Category Tile Grid Block - (CF-030) [2]': (args) => ({
      container: {
        gridTemplateColumns: {base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'},
        gap: {base: 0.75, md: 1},
      },
    }),
    'Form Block - (CF-058)': (args) => ({
      container: {
        paddingTop: {base: 8, md: args.component.paddingTop || 21.25},
        paddingBottom: {base: 8, md: args.component.paddingBottom || 21.25},
        sx: {
          '.component--rich-text-block .amb-markup': {
            display: 'flex',
            flexDirection: {base: 'column', md: 'row'},
            gap: {base: 0, md: 37.5},
            alignItems: {base: 'normal', md: 'flex-end'},
          },
          '.component--rich-text-block .amb-markup h1': {
            fontSize: {base: '4.75xl', md: '7xl'},
            fontWeight: {base: '400', md: '500'},
            lineHeight: {base: '120%', md: '100%'},
            flex: '1',
          },
          '.component--rich-text-block .amb-markup p': {
            fontSize: {base: 'md', md: 'xl'},
            fontWeight: '400',
            lineHeight: {base: '160%', md: '150%'},
            flex: '1',
          },
        },
      },
    }),
  },
});
